// T012: Battlefield Model

import type {
  Battlefield,
  BattlefieldSlot,
  BattlePosition,
  Card,
} from '../../types';

export function createBattlefield(): Battlefield {
  return {
    player1Side: {
      frontLine: createBattleLine('player1', 'front'),
      backLine: createBattleLine('player1', 'back'),
    },
    player2Side: {
      frontLine: createBattleLine('player2', 'front'),
      backLine: createBattleLine('player2', 'back'),
    },
  };
}

function createBattleLine(
  side: 'player1' | 'player2',
  row: 'front' | 'back'
): BattlefieldSlot[] {
  return [1, 2, 3, 4, 5].map((column) => ({
    position: { side, row, column: column as 1 | 2 | 3 | 4 | 5 },
    unit: null,
  }));
}

export function getSlotAt(
  battlefield: Battlefield,
  position: BattlePosition
): BattlefieldSlot | null {
  const side =
    position.side === 'player1'
      ? battlefield.player1Side
      : battlefield.player2Side;
  const line = position.row === 'front' ? side.frontLine : side.backLine;
  return line[position.column - 1] ?? null;
}

export function getUnitAt(
  battlefield: Battlefield,
  position: BattlePosition
): Card | null {
  const slot = getSlotAt(battlefield, position);
  return slot?.unit ?? null;
}

export function placeUnit(
  battlefield: Battlefield,
  position: BattlePosition,
  unit: Card
): Battlefield {
  const newBattlefield = { ...battlefield };
  const side =
    position.side === 'player1' ? 'player1Side' : 'player2Side';
  const line = position.row === 'front' ? 'frontLine' : 'backLine';

  newBattlefield[side] = {
    ...newBattlefield[side],
    [line]: newBattlefield[side][line].map((slot, index) =>
      index === position.column - 1
        ? {
            ...slot,
            unit: { ...unit, location: 'battlefield', position },
          }
        : slot
    ),
  };

  return newBattlefield;
}

export function removeUnit(
  battlefield: Battlefield,
  position: BattlePosition
): Battlefield {
  const newBattlefield = { ...battlefield };
  const side =
    position.side === 'player1' ? 'player1Side' : 'player2Side';
  const line = position.row === 'front' ? 'frontLine' : 'backLine';

  newBattlefield[side] = {
    ...newBattlefield[side],
    [line]: newBattlefield[side][line].map((slot, index) =>
      index === position.column - 1 ? { ...slot, unit: null } : slot
    ),
  };

  return newBattlefield;
}

export function moveUnit(
  battlefield: Battlefield,
  from: BattlePosition,
  to: BattlePosition
): Battlefield {
  const unit = getUnitAt(battlefield, from);
  if (!unit) return battlefield;

  let newBattlefield = removeUnit(battlefield, from);
  newBattlefield = placeUnit(newBattlefield, to, unit);

  return newBattlefield;
}

export function isSlotEmpty(
  battlefield: Battlefield,
  position: BattlePosition
): boolean {
  return getUnitAt(battlefield, position) === null;
}

export function getAllUnitsForPlayer(
  battlefield: Battlefield,
  playerSide: 'player1' | 'player2'
): Card[] {
  const side = playerSide === 'player1'
    ? battlefield.player1Side
    : battlefield.player2Side;

  const units: Card[] = [];

  for (const slot of [...side.frontLine, ...side.backLine]) {
    if (slot.unit) {
      units.push(slot.unit);
    }
  }

  return units;
}

export function getOpponentUnitsInFront(
  battlefield: Battlefield,
  position: BattlePosition
): Card[] {
  const opponentSide = position.side === 'player1' ? 'player2' : 'player1';
  const units: Card[] = [];

  // Check opponent's front line at the same column
  const frontPosition: BattlePosition = {
    side: opponentSide,
    row: 'front',
    column: position.column,
  };
  const frontUnit = getUnitAt(battlefield, frontPosition);
  if (frontUnit) {
    units.push(frontUnit);
  }

  // If position is in back row, also check opponent's back row
  if (position.row === 'back') {
    const backPosition: BattlePosition = {
      side: opponentSide,
      row: 'back',
      column: position.column,
    };
    const backUnit = getUnitAt(battlefield, backPosition);
    if (backUnit) {
      units.push(backUnit);
    }
  }

  return units;
}
